import { forwardRef } from 'react'
import { cn } from '@/shared/lib/utils'

/**
 * Los placeholders de carga de la app, con su barrido.
 *
 * Réplica de `shared/ui/skeleton.tsx`, `skeleton-image.tsx` y `skeleton-text.tsx`. Allá el brillo
 * es un degradado de `react-native-svg` movido por `Animated`; acá es un pseudo-elemento con
 * `linear-gradient` y una animación CSS (`cap-shimmer`, en `theme/cap-preview.css`) que corre en el
 * compositor. El movimiento es el mismo: 1200 ms, de un borde al otro, en bucle.
 *
 * ## Las tres piezas son tres, no una con props
 *
 * `CapSkeleton` es el bloque; `CapSkeletonImage` es el mismo bloque con el gris azulado —más
 * oscuro— porque una imagen ocupa mucha superficie y con el gris del texto la pantalla entera se ve
 * lavada; `CapSkeletonText` es la cápsula, que es lo que hace que el bloque se lea como «acá va a
 * haber una línea escrita» y no como un vacío. Son las mismas tres de la app y por el mismo motivo.
 */
export type CapSkeletonProps = React.ComponentPropsWithoutRef<'div'>

export const CapSkeleton = forwardRef<HTMLDivElement, CapSkeletonProps>(function CapSkeleton(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      aria-hidden
      className={cn(
        'cap-shimmer cap-bg-surface-skeleton relative overflow-hidden rounded-[var(--cap-radius-card)]',
        className
      )}
      {...props}
    />
  )
})

export const CapSkeletonImage = forwardRef<HTMLDivElement, CapSkeletonProps>(
  function CapSkeletonImage({ className, ...props }, ref) {
    return (
      <CapSkeleton
        ref={ref}
        className={cn(
          'cap-bg-surface-skeleton-image h-full w-full rounded-[var(--cap-radius-lg)]',
          className
        )}
        {...props}
      />
    )
  }
)

export const CapSkeletonText = forwardRef<HTMLDivElement, CapSkeletonProps>(
  function CapSkeletonText({ className, ...props }, ref) {
    return (
      <CapSkeleton ref={ref} className={cn('h-full w-full rounded-full', className)} {...props} />
    )
  }
)
